"use client";

import { useEffect, useRef, useState, type CSSProperties } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import Moon01Icon from "@hugeicons/core-free-icons/Moon01Icon";
import Sun01Icon from "@hugeicons/core-free-icons/Sun01Icon";
import { useTheme } from "@/components/providers/theme-provider";
import type { AccentTheme } from "@/lib/site-content";
import { useColorTheme } from "@/components/providers/color-theme-provider";
import { cn } from "@/lib/utils";

const ZAPIFY_GRADIENT = "linear-gradient(90deg, #ff8c42, #ff4e8e, #a06af9)";

const ACCENTS: { key: AccentTheme; label: string; swatch: CSSProperties }[] = [
  { key: "zapify", label: "Zapify", swatch: { background: ZAPIFY_GRADIENT } },
  { key: "blue", label: "Blue", swatch: { backgroundColor: "#2563eb" } },
  { key: "orange", label: "Orange", swatch: { backgroundColor: "#f97316" } },
  { key: "green", label: "Green", swatch: { backgroundColor: "#22c55e" } },
  { key: "yellow", label: "Yellow", swatch: { backgroundColor: "#facc15" } },
  { key: "red", label: "Red", swatch: { backgroundColor: "#ef4444" } },
];

const HOVER_LEAVE_MS = 180;

export function ThemeToggle() {
  const { resolvedTheme, setTheme } = useTheme();
  const { themeColor, setThemeColor, previewThemeColor } = useColorTheme();
  const [mounted, setMounted] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  const isDark = resolvedTheme === "dark";

  const clearLeaveTimer = () => {
    if (leaveTimer.current) {
      clearTimeout(leaveTimer.current);
      leaveTimer.current = null;
    }
  };

  const onWrapperEnter = () => {
    clearLeaveTimer();
    setPanelOpen(true);
  };

  const onWrapperLeave = () => {
    clearLeaveTimer();
    leaveTimer.current = setTimeout(() => {
      setPanelOpen(false);
      previewThemeColor(null);
    }, HOVER_LEAVE_MS);
  };

  return (
    <div className="relative" onMouseEnter={onWrapperEnter} onMouseLeave={onWrapperLeave}>
      <button
        type="button"
        onClick={() => mounted && setTheme(isDark ? "light" : "dark")}
        aria-label={
          !mounted ? "Toggle light or dark mode" : isDark ? "Switch to light mode" : "Switch to dark mode"
        }
        aria-expanded={panelOpen}
        aria-haspopup="dialog"
        className={cn(
          "flex size-10 shrink-0 items-center justify-center rounded-full",
          "border border-border/70 bg-card/90 text-foreground",
          "transition-colors hover:bg-muted/80",
          "outline-none focus-visible:ring-2 focus-visible:ring-ring"
        )}
      >
        {!mounted ? (
          <HugeiconsIcon icon={Moon01Icon} size={18} color="currentColor" className="opacity-40" aria-hidden />
        ) : isDark ? (
          <HugeiconsIcon
            icon={Sun01Icon}
            size={18}
            color="currentColor"
            className="text-amber-300"
            aria-hidden
          />
        ) : (
          <HugeiconsIcon icon={Moon01Icon} size={18} color="currentColor" aria-hidden />
        )}
      </button>

      <div
        role="dialog"
        aria-label="Accent color"
        aria-hidden={!panelOpen}
        className={cn(
          /* pt-2 bridges the gap under the button so hover isn’t lost moving to the card */
          "absolute right-0 top-full z-50 w-max min-w-[13.5rem] max-w-[min(18rem,calc(100vw-1.5rem))] origin-top-right overflow-hidden rounded-2xl pt-2",
          /* Frosted panel: readable fill + blur (gradients alone read as “empty” on many GPUs) */
          "border border-black/[0.08] bg-white shadow-[0_12px_42px_-14px_rgb(0_0_0/0.14)]",
          "dark:border-white/[0.10] dark:bg-neutral-950 dark:shadow-[0_18px_50px_-14px_rgb(0_0_0/0.65)]",
          "transition-all duration-150",
          panelOpen
            ? "pointer-events-auto scale-100 opacity-100"
            : "pointer-events-none scale-95 opacity-0"
        )}
      >
        <div className="border-b border-black/[0.07] bg-gradient-to-b from-black/[0.02] to-transparent px-4 py-3 dark:border-white/[0.1] dark:from-white/[0.04]">
          <p className="text-base font-semibold tracking-tight text-foreground">Accent color</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Hover swatches to preview; click to save. Use the sun/moon button for light or dark.
          </p>
        </div>
        <div className="flex flex-nowrap items-center justify-center gap-1.5 bg-black/[0.015] px-3 py-2.5 dark:bg-white/[0.03]">
          {ACCENTS.map(({ key, label, swatch }) => {
            const active = themeColor === key;
            return (
              <button
                key={key}
                type="button"
                title={label}
                aria-label={`${label} accent`}
                aria-pressed={active}
                onMouseEnter={() => previewThemeColor(key)}
                onClick={() => setThemeColor(key, "navbar_desktop_theme_popover")}
                className={cn(
                  "flex size-6 shrink-0 items-center justify-center rounded-full border-2 transition-transform hover:scale-105",
                  active ? "border-foreground scale-105 shadow-sm" : "border-transparent"
                )}
                style={swatch}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
